import React, { FC } from "react";
import styled from "styled-components";

import { ICircleFilledIcon } from "@adsk/alloy-react-icon";
import { useLocale } from "@adsk/alloy-react-locale";
import ProgressBar from "@adsk/alloy-react-progress-bar";
import Theme from "@adsk/alloy-react-theme";
import Tooltip, { InfoPopover } from "@adsk/alloy-react-tooltip";

import i18n from "../../i18n";
import { UNSPECIFIED_STATUS_COLOR } from "../../models/statusSets";
import PercentCompleteText from "./PercentCompleteText";

// Shared visual pieces for the "status name row" (row 1) and "progress
// bar + percent text" row (row 2) used by both `StatusWithProgressDisplay`
// (pure display) and `EditableStatusField` (wraps row 1 in its own
// InlineEdit). Kept editability-agnostic on purpose — any inline-edit
// affordance is the caller's responsibility, not this file's, so this stays
// safe to reuse from a pure display component without reintroducing edit
// capability into it.

export const StatusWithProgressRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const StatusNameRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const ColorIndicator = styled.div<{ $color: string }>`
  align-self: stretch;
  background: ${({ $color }) => $color};
  border-radius: 4px;
  flex: 0 0 4px;
`;

export type StatusNameSize = "medium" | "small";

const typographyBySize: Record<StatusNameSize, keyof typeof Theme.typography> = {
  medium: "bodyMedium",
  small: "labelSmall",
};

const ProgressStatusName = styled.div<{
  $isUnspecified: boolean;
  $statusNameSize: StatusNameSize;
}>`
  ${({ $statusNameSize }) => Theme.typography[typographyBySize[$statusNameSize]]}
  color: ${Theme.colors.charcoal900};
  ${({ $isUnspecified }) =>
    $isUnspecified && `color: ${Theme.colors.charcoal500}; font-style: italic;`}
`;

export type StatusNameDisplayProps = {
  statusColor?: string;
  displayName?: string;
  isUnspecified: boolean;
  statusNameSize?: StatusNameSize;
};

export const StatusNameDisplay: FC<StatusNameDisplayProps> = ({
  statusColor,
  displayName,
  isUnspecified,
  statusNameSize = "medium",
}) => (
  <StatusNameRow>
    <ColorIndicator $color={statusColor ?? UNSPECIFIED_STATUS_COLOR} />
    <ProgressStatusName
      $isUnspecified={isUnspecified}
      $statusNameSize={statusNameSize}
    >
      {displayName}
    </ProgressStatusName>
  </StatusNameRow>
);

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  width: 100%;
`;

const PercentLabelRow = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
`;

// The "N% completed" text is rendered by `PercentCompleteText`, which
// clamps internally, so the label and bar always agree even though this
// helper is intentionally not shared with it (keeping the two surfaces
// decoupled).
export const clampPercent = (value: number): number =>
  Math.round(Math.min(100, Math.max(0, value)));

export type PercentCompleteDisplayProps = {
  /** Already resolved to a definite number — callers decide null-handling
   * (e.g. omit this component entirely, or default to 0) before rendering. */
  percent: number;
  /**
   * When true, suppresses the horizontal `ProgressBar` and renders only the
   * "N% completed" text + info tooltip. Used by dense list surfaces where
   * the bar is redundant in a row.
   */
  hideProgressBar?: boolean;
};

export const PercentCompleteDisplay: FC<PercentCompleteDisplayProps> = ({
  percent,
  hideProgressBar,
}) => {
  const { T } = useLocale(i18n);
  return (
    <ProgressSection>
      {!hideProgressBar && (
        <ProgressBar data-testid="StatusPercentCompleteBar" now={percent} />
      )}
      <PercentLabelRow>
        <PercentCompleteText
          percent={percent}
          data-testid="StatusPercentCompleteLabel"
        />
        <InfoPopover
          variant={Tooltip.VARIANTS.DARK}
          placement={Tooltip.PLACEMENTS.TOP}
          message={T.translate("percentCompleteBar.tooltip")?.toString()}
          style={{ display: "flex", alignItems: "center" }}
          renderIcon={(iconProps) => (
            <ICircleFilledIcon
              {...iconProps}
              data-testid="StatusPercentCompleteBarInfo"
              style={{ color: Theme.colors.charcoal500 }}
            />
          )}
        />
      </PercentLabelRow>
    </ProgressSection>
  );
};
